import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Fade from "embla-carousel-fade";
import { ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Database } from "@/integrations/supabase/types";

type CourseImage = Database["public"]["Tables"]["course_images"]["Row"];

interface CourseImageCarouselProps {
  images: CourseImage[];
  courseName: string;
  className?: string;
}

export const CourseImageCarousel = ({ 
  images, 
  courseName, 
  className 
}: CourseImageCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      duration: 20, // 더 부드러운 전환을 위해 duration 설정
    },
    [Fade()]
  );
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [imageLoadStates, setImageLoadStates] = useState<boolean[]>([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();

    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  // 이미지 로딩 상태 초기화
  useEffect(() => {
    setImageLoadStates(new Array(images.length).fill(false));
  }, [images.length]);

  const handleImageLoad = (index: number) => {
    setImageLoadStates(prev => {
      const newStates = [...prev];
      newStates[index] = true;
      return newStates;
    });
  };


  // 5초마다 자동 슬라이드
  useEffect(() => {
    if (!emblaApi || images.length <= 1) return;

    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [emblaApi, images.length]);

  if (!images || images.length === 0) {
    return (
      <div className={cn(
        "relative w-full h-full bg-gray-200 rounded-lg flex items-center justify-center",
        className
      )}>
        <div className="text-center text-gray-500">
          <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">이미지가 없습니다</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative group", className)}>
      <div className="absolute inset-0 overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="flex h-full">
          {images.map((image, index) => (
            <div key={image.id} className="flex-[0_0_100%] min-w-0 h-full">
              <div className="relative w-full h-full bg-gray-200">
                {!imageLoadStates[index] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <img
                  src={image.image_url}
                  alt={image.alt_text || `${courseName} 이미지 ${index + 1}`}
                  className={cn(
                    "w-full h-full object-cover transition-opacity duration-300",
                    imageLoadStates[index] ? "opacity-100" : "opacity-0"
                  )}
                  onLoad={() => handleImageLoad(index)}
                  onError={() => handleImageLoad(index)}
                />
                
                {/* 그라데이션 오버레이 */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                
                {/* 이미지 순서 표시 */}
                <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded-full text-xs font-medium">
                  {index + 1} / {images.length}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 이전/다음 버튼 */}
      {images.length > 1 && (
        <>
          <Button
            variant="outline"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 hover:bg-white border-white/50 shadow-lg"
            onClick={scrollPrev}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">이전 이미지</span>
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 hover:bg-white border-white/50 shadow-lg"
            onClick={scrollNext}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">다음 이미지</span>
          </Button>
        </>
      )}

      {/* 도트 인디케이터 */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                index === selectedIndex
                  ? "bg-white w-6"
                  : "bg-white/50 hover:bg-white/75"
              )}
              onClick={() => scrollTo(index)}
              aria-label={`이미지 ${index + 1}번으로 이동`}
            />
          ))}
        </div>
      )}
    </div>
  );
};